// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import specialOfferApi from './specialOffer.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchSpecialOfferApi = createAsyncThunk(
    'shopSaga/specialOffer/fetchSpecialOfferApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await specialOfferApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const createSpecialOfferApi = createAsyncThunk(
    'shopSaga/specialOffer/createSpecialOfferApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await specialOfferApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeSpecialOfferApi = createAsyncThunk(
    'shopSaga/specialOffer/storeSpecialOfferApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const specialOffer = await specialOfferApi.store(values);
            alertSwal('success', 'SpecialOffer created successfully!');

            return specialOffer;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showSpecialOfferApi = createAsyncThunk(
    'shopSaga/specialOffer/showSpecialOfferApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await specialOfferApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateSpecialOfferApi = createAsyncThunk(
    'shopSaga/specialOffer/updateSpecialOfferApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await specialOfferApi.update(id, values);
            alertSwal('success', 'SpecialOffer updated successfully!');

            const { ranks } = state.shopSaga.specialOfferMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteSpecialOfferApi = createAsyncThunk('shopSaga/specialOffer/deleteSpecialOfferApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await specialOfferApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const specialOfferSlice = createSlice({
    name: 'shopSaga/specialOffer',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedSpecialOffer(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSpecialOfferApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchSpecialOfferApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(createSpecialOfferApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createSpecialOfferApi.rejected, () => {
                //
            });

        builder
            .addCase(storeSpecialOfferApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeSpecialOfferApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showSpecialOfferApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showSpecialOfferApi.rejected, () => {
                //
            });

        builder
            .addCase(updateSpecialOfferApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateSpecialOfferApi.rejected, () => {
                //
            });

        builder.addCase(deleteSpecialOfferApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedSpecialOffer } = specialOfferSlice.actions;
